import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { IChessSceneState } from '@skillarea/game-chess-phaser';
import {
  Chess,
  boardFromChess,
  getKingInCheckSquare,
  DEFAULT_TURN_DURATION_MS,
  type Color,
} from '@skillarea/game-chess';
import { GameLayout } from '@skillarea/game-layout';

import { usePhaserGameLoader } from '../use-phaser-game-loader';

import { getChessLayoutProps } from './get-chess-layout-props';
import styles from './ChessGame.module.scss';
import type { IChessGameProps } from './types';

import { playGameStartSound } from '~/lib/game-sounds';
import { GameLoadingBlock } from '~/components/Matchmaking/screens/GameScreen';
import { useGameLayoutLabels } from '~/hooks/useGameLayoutLabels';
import { useLocalizedGameName } from '~/hooks/useLocalizedGame';

export type { IChessGameResult } from './types';

export const ChessGame: FC<IChessGameProps> = ({
  fen,
  isGameOver,
  onMove,
  onSurrender,
  player1: player1Info,
  player1TimeLeftMs,
  player2: player2Info,
  player2TimeLeftMs,
  playerColor,
  rulesContent = '',
  turnDurationMs = DEFAULT_TURN_DURATION_MS,
}) => {
  const gameLayoutLabels = useGameLayoutLabels();
  const gameName = useLocalizedGameName('classic-chess', 'Chess');
  const containerRef = useRef<globalThis.HTMLDivElement>(null);

  const derived = useMemo(() => {
    const chess = new Chess(fen);
    return {
      board: boardFromChess(chess),
      currentTurn: chess.turn() as Color,
      kingInCheckSquare: getKingInCheckSquare(fen),
    };
  }, [fen]);

  const isMyTurn = !isGameOver && derived.currentTurn === playerColor;

  type TChessPhaserGame = {
    updateState: (state: IChessSceneState) => void;
    destroy: () => void;
  };
  type TChessPhaserGameConstructor = new (args: {
    parent: globalThis.HTMLDivElement;
    onMove: (
      from: { row: number; col: number },
      to: { row: number; col: number },
      promotion?: 'Q' | 'R' | 'B' | 'N',
    ) => void;
  }) => TChessPhaserGame;

  const gameRef = useRef<TChessPhaserGame | null>(null);
  const onMoveRef = useRef(onMove);
  const [gameReady, setGameReady] = useState(false);
  onMoveRef.current = onMove;

  const onLoadedRef = useRef(() => {
    playGameStartSound();
    setGameReady(true);
  });
  const handleLoaded = useCallback(() => {
    onLoadedRef.current();
  }, []);

  const loadGame = useCallback(
    async (parent: globalThis.HTMLDivElement): Promise<TChessPhaserGame> => {
      const mod = await import('@skillarea/game-chess-phaser');
      const Game =
        mod.ChessPhaserGame as unknown as TChessPhaserGameConstructor;
      const game = new Game({
        parent,
        onMove: (from, to, promotion) => onMoveRef.current(from, to, promotion),
      });
      const withReady = game as { whenReady?: () => Promise<void> };
      if (withReady.whenReady) await withReady.whenReady();
      return game;
    },
    [],
  );

  const { isReady } = usePhaserGameLoader({
    containerRef,
    gameRef,
    loadGame,
    onLoaded: handleLoaded,
  });

  useEffect(() => {
    if (!gameRef.current) return;
    gameRef.current.updateState({
      board: derived.board,
      fen,
      currentTurn: derived.currentTurn,
      isMyTurn,
      kingInCheckSquare: derived.kingInCheckSquare,
      playerColor,
    });
  }, [gameReady, fen, derived, isMyTurn, playerColor]);

  useEffect(() => {
    if (!gameReady || !gameRef.current) return;
    const id = window.setTimeout(() => {
      if (gameRef.current) {
        gameRef.current.updateState({
          board: derived.board,
          fen,
          currentTurn: derived.currentTurn,
          isMyTurn,
          kingInCheckSquare: derived.kingInCheckSquare,
          playerColor,
        });
      }
    }, 50);
    return () => window.clearTimeout(id);
  }, [gameReady]);

  const gameContent = (
    <div className={styles.gameWrapper}>
      {!isReady ? <GameLoadingBlock className={styles.gameLoading} /> : null}
      <div className={styles.boardContainer} ref={containerRef} />
    </div>
  );

  const { activePlayerId, player1, player2 } = getChessLayoutProps({
    currentTurn: derived.currentTurn,
    isGameOver,
    player1: player1Info,
    player2: player2Info,
    playerColor,
  });

  const symbolPlayer1 = <span className={styles.symbolPlayer1} />;
  const symbolPlayer2 = <span className={styles.symbolPlayer2} />;
  const symbolFor0 = symbolPlayer2;
  const symbolFor1 = symbolPlayer1;
  const player1WithSymbol = {
    ...player1,
    symbolContent: playerColor === 'w' ? symbolFor0 : symbolFor1,
  };
  const player2WithSymbol = {
    ...player2,
    symbolContent: playerColor === 'w' ? symbolFor1 : symbolFor0,
  };

  return (
    <GameLayout
      activePlayerId={activePlayerId}
      gameContent={gameContent}
      gameName={gameName}
      labels={gameLayoutLabels}
      onSurrender={onSurrender}
      player1={player1WithSymbol}
      player1TimeLeftMs={player1TimeLeftMs}
      player2={player2WithSymbol}
      player2TimeLeftMs={player2TimeLeftMs}
      rulesContent={rulesContent}
      turnDurationMs={turnDurationMs}
    />
  );
};
